import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Alert, Card, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { listOffers, errorText } from "./api";
import { formatDay, formatEuro } from "../../../serviceProvider/components/workReports/format";
import { changeOrderState, offerStatus } from "./labels";
import "./portal.css";

/** The customer's offers and Nachträge; open ones first. */
const PortalOffers = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const history = useHistory();
  const [state, setState] = useState({ loading: true, offers: [], changeOrders: [] });

  useEffect(() => {
    listOffers()
      .then(res => setState({ loading: false, ...res.data.data }))
      .catch(error =>
        setState({
          loading: false,
          offers: [],
          changeOrders: [],
          error: errorText(error, t("The offers could not be loaded."))
        })
      );
  }, [t]);

  const byOpenFirst = (a, b) => Number(b.canDecide) - Number(a.canDecide);

  const offerColumns = [
    {
      title: t("Offer"),
      dataIndex: "number",
      mobile: "title",
      render: (value, record) => (
        <Link to={`/client/offers/${record._id}`}>
          {[value, record.title].filter(Boolean).join(" – ")}
        </Link>
      )
    },
    { title: t("Date"), dataIndex: "date", mobile: "subtitle", render: value => formatDay(value) },
    { title: t("Project"), dataIndex: ["project", "name"], render: value => value || "–" },
    {
      title: t("Status"),
      dataIndex: "status",
      mobile: "badge",
      render: value => {
        const tag = offerStatus(t, value);
        return <Tag color={tag.color}>{tag.text}</Tag>;
      }
    },
    {
      title: t("Amount (gross)"),
      dataIndex: ["totals", "gross"],
      align: "right",
      render: value => formatEuro(value, lng)
    }
  ];

  const changeOrderColumns = [
    {
      title: t("Change order"),
      dataIndex: "number",
      mobile: "title",
      render: (value, record) => (
        <Link
          to={`/client/offers/change-orders/${record._id}`}
        >{`${value} – ${record.title}`}</Link>
      )
    },
    {
      title: t("Answer by"),
      dataIndex: "responseDue",
      mobile: "subtitle",
      render: value => (value ? formatDay(value) : "–")
    },
    {
      title: t("Status"),
      dataIndex: "state",
      mobile: "badge",
      render: value => {
        const tag = changeOrderState(t, value);
        return <Tag color={tag.color}>{tag.text}</Tag>;
      }
    },
    {
      title: t("Amount (gross)"),
      dataIndex: ["totals", "gross"],
      align: "right",
      render: value => formatEuro(value, lng)
    }
  ];

  return (
    <div className="portal-page">
      {state.error && <Alert className="portal-gap" type="error" showIcon message={state.error} />}
      <Card title={t("Offers")} className="portal-card portal-gap">
        <Typography.Paragraph type="secondary">
          {t(
            "Open an offer to read it, download the PDF and accept it with your signature or decline it."
          )}
        </Typography.Paragraph>
        <ResponsiveTable
          rowKey="_id"
          loading={state.loading}
          columns={offerColumns}
          dataSource={[...state.offers].sort(byOpenFirst)}
          pagination={false}
          mobileAvatar={false}
          onRow={record => ({ onClick: () => history.push(`/client/offers/${record._id}`) })}
          locale={{ emptyText: t("No offers yet.") }}
        />
      </Card>
      {state.changeOrders.length > 0 && (
        <Card title={t("Change orders")} className="portal-card">
          <ResponsiveTable
            rowKey="_id"
            columns={changeOrderColumns}
            dataSource={[...state.changeOrders].sort(byOpenFirst)}
            pagination={false}
            mobileAvatar={false}
            onRow={record => ({
              onClick: () => history.push(`/client/offers/change-orders/${record._id}`)
            })}
          />
        </Card>
      )}
    </div>
  );
};

export default PortalOffers;
